'use client';
/**
 * ANNUAIRE — créer une fiche en DEUX ÉTAPES (REGLES §2.0), ou la modifier.
 *
 *  1. Le TYPE d'abord : des tuiles, et aucun champ avant d'en avoir choisi une.
 *  2. Le formulaire PROPRE au type : seuls ses champs, les obligatoires marqués,
 *     « Enregistrer » désactivé tant qu'il en manque.
 *
 * Pendant la frappe : le NOM passe en MAJUSCULES, le Prénom prend une
 * majuscule à chaque mot, et le moteur vérifie la saisie (`verifier`, sans
 * écriture) — erreurs, doublons, inversion NOM/Prénom (bandeau + « Inverser »).
 * À l'enregistrement, un doublon ouvre la modale « Ouvrir la fiche existante /
 * Créer quand même » (motif obligatoire).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGed } from '@/lib/store';
import { I, inputStyle, Selecteur, Pastille, Modale, FieldError } from '@/components/ui';
import { lbl } from '../ressources/commun';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import {
  TUILES, TITRES, CATEGORIES_PROFESSIONNEL, SOUS_TYPES_ETABLISSEMENT, ROLES_LABORATOIRE, ROLES_SOCLE_LABORATOIRE, CHAMPS_DU_TYPE,
  manquesSaisie, requeteCreation, requeteVerification, nomPendantFrappe, prenomPendantFrappe, libelleChamp,
  type Tuile, type Saisie,
} from '@/lib/annuaire';
import {
  ChampReferentiel, ChoixFiche, Coordonnees, ModaleDoublons, VerdictSaisie, ecrire, lire, telsEditables, mailsEditables,
  ErreurAnnuaire, type Referentiels, type Verdict, type Candidat,
} from './commun';

type Fiche = Record<string, unknown> & { id: string; entite: 'organisation' | 'personne' };

/** La tuile qui correspond à une fiche existante (modification). */
export function tuileDeFiche(f: Fiche): Tuile {
  if (f.entite === 'personne') return f.categorie === 'medecin' ? 'medecin' : f.categorie === 'interlocuteur_fournisseur' ? 'interlocuteur' : 'professionnel';
  if (f.type === 'laboratoire') return 'laboratoire';
  if (f.type === 'etablissement_sante' || f.type === 'cabinet') return 'etablissement';
  return 'fournisseur';
}

/** Les champs proposés en MODIFICATION : ceux du type, sans le rattachement
 *  d'un interlocuteur (il se gère dans ses affiliations). */
function champsEdition(f: Fiche, tuile: Tuile): string[] {
  const base = CHAMPS_DU_TYPE[tuile].filter((c) => !['org_id', 'fonction', 'categorie'].includes(c));
  if (f.entite === 'organisation') {
    const roles = (f.roles as string[]) || [];
    if (f.type === 'cabinet') return base.filter((c) => c !== 'sous_type');
    if (tuile === 'laboratoire' && roles.includes('fournisseur') && !base.includes('categorie_achat')) return [...base, 'categorie_achat'];
  }
  return base;
}

const saisieDeFiche = (f: Fiche): Saisie => ({
  ...(Object.fromEntries(Object.entries(f).filter(([, v]) => typeof v === 'string' || Array.isArray(v))) as Saisie),
  telephones: telsEditables(f.telephones as never), emails: mailsEditables(f.emails as never), echographiste: f.echographiste === true,
});

export function FormulaireFiche({ refs, fiche, tuileInitiale, prerempli, onFait, onClose, onOuvrir }: {
  refs: Referentiels; fiche?: Fiche | null; tuileInitiale?: Tuile; prerempli?: Partial<Saisie> & { org_libelle?: string };
  onFait: (id: string) => void; onClose: () => void; onOuvrir: (id: string) => void;
}) {
  const { showToast } = useGed();
  const edition = !!fiche;
  const [tuile, setTuile] = useState<Tuile | null>(fiche ? tuileDeFiche(fiche) : tuileInitiale || null);
  const [s, setS] = useState<Saisie>(() => (fiche ? saisieDeFiche(fiche) : { pays: 'Tunisie', telephones: [{ indicatif: '+216', numero: '', principal: true }], emails: [], roles: [], ...prerempli }));
  const [verdict, setVerdict] = useState<(Verdict & { doublons?: Candidat[] }) | null>(null);
  const [refus, setRefus] = useState<{ message: string; verdict?: Partial<Verdict> } | null>(null);
  const [doublons, setDoublons] = useState<{ code: string; candidats: Candidat[] } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maj = (p: Partial<Saisie>) => setS((x) => ({ ...x, ...p }));

  /* Vérification par le moteur pendant la saisie (sans écriture). */
  useEffect(() => {
    if (!tuile) return undefined;
    const corps = requeteVerification(tuile, s);
    if (!corps) return undefined;
    if (minuterie.current) clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => {
      ecrire<Verdict & { doublons: Candidat[] }>('ged/annuaire/verifier', { ...corps, ...(fiche ? { id: fiche.id } : {}) })
        .then(setVerdict).catch(() => setVerdict(null));
    }, 600);
    return () => { if (minuterie.current) clearTimeout(minuterie.current); };
  }, [tuile, s, fiche]);

  const manques = useMemo(() => (tuile && !edition ? manquesSaisie(tuile, s) : []), [tuile, s, edition]);
  const inversion = verdict?.propositions?.some((p) => p.code === 'inversion_probable');
  const champs = tuile ? (edition && fiche ? champsEdition(fiche, tuile) : CHAMPS_DU_TYPE[tuile]) : [];
  const montre = (c: string) => champs.includes(c);

  const enregistrer = async (forcer?: { motif: string }) => {
    if (!tuile) return;
    setEnCours(true); setRefus(null);
    try {
      const { route, corps } = requeteCreation(tuile, s);
      const chemin = edition && fiche ? `${route}/${encodeURIComponent(fiche.id)}` : route;
      const corpsFinal: Record<string, unknown> = edition ? { ...corps } : corps;
      if (edition) { delete corpsFinal.categorie; delete corpsFinal.type; delete corpsFinal.affiliation; }
      if (forcer) corpsFinal.doublon = { forcer: true, motif: forcer.motif };
      const r = await ecrire<{ id: string }>(chemin, corpsFinal);
      showToast(edition ? 'Fiche enregistrée.' : `Fiche créée (${r.id}).`);
      setDoublons(null);
      onFait(r.id);
    } catch (e) {
      const err = e as ErreurAnnuaire;
      const code = String(err.details?.code || '');
      if (['doublon_probable', 'doublon_bloquant', 'proposer_affiliation'].includes(code)) setDoublons({ code, candidats: (err.details?.doublons as Candidat[]) || [] });
      else setRefus({ message: err.message, verdict: err.details as Partial<Verdict> | undefined });
    } finally { setEnCours(false); }
  };

  const affilier = async (perId: string) => {
    setEnCours(true);
    try {
      await ecrire('ged/annuaire/affiliations', { per_id: perId, org_id: s.org_id, role: 'interlocuteur', fonction: s.fonction });
      showToast('Affiliation ajoutée à la fiche existante.');
      setDoublons(null); onFait(perId);
    } catch (e) { setRefus({ message: (e as Error).message }); } finally { setEnCours(false); }
  };

  const titre = edition ? `Modifier — ${String(fiche?.raison_sociale || fiche?.nom_complet || fiche?.id)}`
    : tuile ? `Créer — ${TUILES.find((t) => t.code === tuile)?.libelle}` : 'Créer une fiche';

  /* ── Étape 1 : les tuiles ─────────────────────────────────────────────── */
  if (!tuile) {
    const permises = TUILES.filter((t) => (t.permission === 'correspondant' ? refs.moi.ecrire_correspondant : refs.moi.ecrire_fournisseur));
    return (
      <Modale titre={titre} sousTitre="Choisissez d’abord le type de contact : le formulaire suivra." onClose={onClose} largeur={760} sansSaisie
        pied={<PiedActions taille="sm" annuler={{ onClick: onClose }} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }} role="list">
          {permises.map((t) => (
            <button key={t.code} type="button" role="listitem" onClick={() => setTuile(t.code)}
              style={{ display: 'grid', gap: 6, textAlign: 'start', padding: 14, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', font: 'inherit' }}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, color: 'var(--text-strong)' }}><I n={t.icone} s={16} />{t.libelle}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{t.aide}</span>
            </button>
          ))}
        </div>
        {permises.length < TUILES.length && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '12px 0 0' }}>Les autres types relèvent d’un autre rôle (accueil pour les correspondants, achats pour les fournisseurs).</p>}
      </Modale>
    );
  }

  /* ── Étape 2 : le formulaire du type ──────────────────────────────────── */
  const texte = (c: keyof Saisie, label: string, opts: { obligatoire?: boolean; placeholder?: string; format?: (v: string) => string; aide?: string } = {}) => (
    <div>
      <label style={lbl} htmlFor={`an-${c}`}>{label}{opts.obligatoire ? ' *' : ''}</label>
      <input id={`an-${c}`} value={String(s[c] ?? '')} placeholder={opts.placeholder}
        onChange={(e) => maj({ [c]: opts.format ? opts.format(e.target.value) : e.target.value } as Partial<Saisie>)} style={inputStyle} />
      {opts.aide && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{opts.aide}</div>}
    </div>
  );
  const exigeSpe = tuile === 'medecin' || ['dentiste', 'biologiste'].includes(String(s.categorie));
  return (
    <>
      <Modale titre={titre} onClose={onClose} largeur={760}
        sousTitre={edition ? `${fiche?.id} — le type est verrouillé (changement réservé à l’Admin qualité).` : 'Les champs marqués * sont obligatoires.'}
        pied={(
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {!edition && <Bouton taille="sm" variante="discret" onClick={() => { setTuile(null); setVerdict(null); }}>Changer de type</Bouton>}
            </span>
            <PiedActions taille="sm" annuler={{ onClick: onClose }}
              principale={{ libelle: edition ? 'Enregistrer' : 'Créer la fiche', libelleChargement: 'Enregistrement…', chargement: enCours, disabled: enCours || manques.length > 0,
                title: manques.length ? `À compléter : ${manques.map(libelleChamp).join(', ')}` : undefined, onClick: () => void enregistrer() }} />
          </div>
        )}>
        <div style={{ display: 'grid', gap: 14 }}>
          {inversion && (
            <div role="status" style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 13, color: 'var(--text-body)' }}><strong>Nom et prénom inversés ?</strong> « {s.nom} » ressemble à un prénom.</span>
              <Bouton taille="sm" variante="contour" onClick={() => maj({ nom: nomPendantFrappe(String(s.prenom || '')), prenom: prenomPendantFrappe(String(s.nom || '')) })}>Inverser</Bouton>
            </div>
          )}
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
            {montre('org_id') && (
              <div style={{ gridColumn: '1 / -1' }}>
                <ChoixFiche id="an-org" label={tuile === 'ligne_service' ? 'Organisation' : 'Fournisseur'} entite="organisation" obligatoire
                  types={tuile === 'interlocuteur' ? ['fournisseur', 'laboratoire', 'pharmacie'] : undefined}
                  valeur={String(s.org_id || '')} libelleInitial={prerempli?.org_libelle} onChange={(id) => maj({ org_id: id })} />
              </div>
            )}
            {montre('categorie') && (
              <div><label style={lbl} htmlFor="an-cat">Catégorie *</label>
                <Selecteur id="an-cat" value={String(s.categorie || '')} onChange={(v) => maj({ categorie: v })} style={inputStyle}>
                  <option value="">—</option>{CATEGORIES_PROFESSIONNEL.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
                </Selecteur></div>
            )}
            {montre('titre') && (
              <div><label style={lbl} htmlFor="an-titre">Titre</label>
                <Selecteur id="an-titre" value={String(s.titre || '')} onChange={(v) => maj({ titre: v })} style={inputStyle}>
                  <option value="">—</option>{TITRES.map((x) => <option key={x} value={x}>{x}</option>)}
                </Selecteur></div>
            )}
            {montre('nom') && texte('nom', 'NOM', { obligatoire: tuile !== 'interlocuteur', format: nomPendantFrappe, placeholder: 'BEN SALEM' })}
            {montre('prenom') && texte('prenom', 'Prénom', { obligatoire: tuile !== 'interlocuteur', format: prenomPendantFrappe, placeholder: 'Mohamed Amine' })}
            {montre('nom_epouse') && texte('nom_epouse', 'Nom d’épouse', { format: nomPendantFrappe })}
            {montre('fonction') && (
              <ChampReferentiel id="an-fonction" label="Fonction" obligatoire liste={refs.fonctions} valeur={String(s.fonction || '')} onChange={(v) => maj({ fonction: v })} />
            )}
            {montre('specialite') && (
              <ChampReferentiel id="an-spe" label="Spécialité" obligatoire={exigeSpe || tuile === 'laboratoire'} liste={refs.specialites} valeur={String(s.specialite || '')} onChange={(v) => maj({ specialite: v })} />
            )}
            {montre('echographiste') && (
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer', alignSelf: 'end', paddingBottom: 8 }}>
                <input type="checkbox" checked={!!s.echographiste} onChange={(e) => maj({ echographiste: e.target.checked })} />
                Échographiste (dépistage prénatal, MoMent)
              </label>
            )}
            {montre('code_cnam') && texte('code_cnam', 'Code CNAM', { obligatoire: !s.derogationCnam, placeholder: '1/26369/82', aide: 'Saisie acceptée avec - ou espaces.' })}
            {montre('code_cnam') && !edition && (
              <div>
                <label style={lbl} htmlFor="an-derog">Dérogation (CNAM inconnu)</label>
                <input id="an-derog" value={String(s.derogationCnam || '')} placeholder="Motif — ex. médecin étranger de passage" onChange={(e) => maj({ derogationCnam: e.target.value })} style={inputStyle} />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Sans code CNAM, un motif de 10 caractères au moins.</div>
              </div>
            )}
            {montre('numero_ordre') && texte('numero_ordre', 'N° d’ordre')}
            {montre('raison_sociale') && texte('raison_sociale', 'Raison sociale', { obligatoire: true })}
            {montre('nom_court') && texte('nom_court', 'Nom court / sigle', { placeholder: 'Ex. SMS Diag' })}
            {montre('roles') && (
              <fieldset style={{ gridColumn: '1 / -1', border: 0, padding: 0, margin: 0 }}>
                <legend style={lbl}>Rôles *</legend>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {ROLES_LABORATOIRE.map((r) => {
                    const socle = ROLES_SOCLE_LABORATOIRE.includes(r.code);
                    return (
                      <label key={r.code} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: socle ? 'default' : 'pointer' }}>
                        <input type="checkbox" checked={socle || (s.roles || []).includes(r.code)} disabled={socle}
                          onChange={(e) => maj({ roles: e.target.checked ? [...(s.roles || []), r.code] : (s.roles || []).filter((x) => x !== r.code) })} />{r.libelle}
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Tout laboratoire est correspondant et confrère ; cochez en plus sous-traitant ou fournisseur s’il y a lieu.</div>
              </fieldset>
            )}
            {montre('responsable_per_id') && (
              <ChoixFiche id="an-resp" label="Biologiste responsable" entite="personne" obligatoire valeur={String(s.responsable_per_id || '')}
                libelleInitial={String((fiche?.responsable as { nom_complet?: string } | null)?.nom_complet || '')}
                onChange={(id) => maj({ responsable_per_id: id })} aide="Une fiche personne : à créer d’abord si elle n’existe pas." />
            )}
            {montre('rattachee_a') && (
              <ChoixFiche id="an-ratt" label="Rattaché à (établissement)" entite="organisation" types={['etablissement_sante']} valeur={String(s.rattachee_a || '')}
                libelleInitial={String((fiche?.rattachee as { raison_sociale?: string } | null)?.raison_sociale || '')} exclure={fiche?.id}
                onChange={(id) => maj({ rattachee_a: id })} aide="Les laboratoires d’un CHU se rattachent à leur établissement." />
            )}
            {montre('sous_type') && (
              <div><label style={lbl} htmlFor="an-st">Sous-type *</label>
                <Selecteur id="an-st" value={String(s.sous_type || '')} onChange={(v) => maj({ sous_type: v })} style={inputStyle}>
                  <option value="">—</option>{SOUS_TYPES_ETABLISSEMENT.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
                </Selecteur></div>
            )}
            {montre('categorie_achat') && (
              <ChampReferentiel id="an-catach" label="Catégorie d’achat" obligatoire liste={refs.categoriesAchat.filter((c) => c.code !== 'a_classer')} valeur={String(s.categorie_achat === 'a_classer' ? '' : s.categorie_achat || '')} onChange={(v) => maj({ categorie_achat: v })} />
            )}
            {montre('pays') && texte('pays', 'Pays', { obligatoire: true, aide: 'Tunisie : matricule fiscal obligatoire.' })}
            {montre('matricule_fiscal') && texte('matricule_fiscal', 'Matricule fiscal', { obligatoire: tuile === 'fournisseur' && (s.pays || 'Tunisie') === 'Tunisie', placeholder: '1094798/A/A/M/000' })}
            {montre('activite') && texte('activite', 'Activité')}
            {montre('libelle') && (
              <ChampReferentiel id="an-lib" label="Libellé" obligatoire liste={refs.libellesService} valeur={String(s.libelle || '')} onChange={(v) => maj({ libelle: v })} />
            )}
            {montre('libelle_libre') && texte('libelle_libre', 'Précision', { placeholder: 'Ex. service commande Sousse' })}
            {montre('adresse') && <div style={{ gridColumn: '1 / -1' }}>{texte('adresse', 'Adresse')}</div>}
            {montre('code_postal') && texte('code_postal', 'Code postal', { placeholder: '5000' })}
            {montre('ville_code') && (
              <ChampReferentiel id="an-ville" label="Ville" obligatoire={['laboratoire', 'etablissement'].includes(tuile)} liste={refs.villes} valeur={String(s.ville_code || '')} onChange={(v) => maj({ ville_code: v })} />
            )}
            {montre('site_web') && texte('site_web', 'Site web', { placeholder: 'www.exemple.tn' })}
          </div>
          {montre('contact') && (
            <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
              <Coordonnees prefixe="an" tels={(s.telephones || []) as never} emails={(s.emails || []) as never} avecUsages={tuile !== 'interlocuteur'}
                onTels={(t) => maj({ telephones: t })} onEmails={(m) => maj({ emails: m })} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Au moins un téléphone <strong>ou</strong> un email. L’email n’est jamais obligatoire.</div>
            </div>
          )}
          {!edition && manques.length > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>À compléter avant d’enregistrer : {manques.map((m) => <Pastille key={m} ton="warning" compacte>{libelleChamp(m)}</Pastille>)}</div>
          )}
          <VerdictSaisie verdict={verdict} libelleChamp={libelleChamp} />
          {verdict?.doublons && verdict.doublons.length > 0 && (
            <div role="status" style={{ fontSize: 12.5, color: 'var(--text-body)', padding: '8px 12px', background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)' }}>
              <strong>Fiche proche déjà dans l’annuaire :</strong>{' '}
              {verdict.doublons.slice(0, 3).map((d, i) => (
                <span key={d.id}>{i ? ' · ' : ''}<Bouton taille="sm" variante="discret" onClick={() => onOuvrir(d.id)}>{d.libelle}</Bouton> ({d.motifs.map((m) => m.libelle).join(', ')})</span>
              ))}
            </div>
          )}
          {refus && (<><FieldError>{refus.message}</FieldError><VerdictSaisie verdict={refus.verdict || null} libelleChamp={libelleChamp} /></>)}
        </div>
      </Modale>
      {doublons && (
        <ModaleDoublons code={doublons.code} candidats={doublons.candidats} enCours={enCours} onClose={() => setDoublons(null)} libelleForcer={edition ? 'Enregistrer quand même' : 'Créer quand même'}
          onOuvrir={(id) => { setDoublons(null); onOuvrir(id); }} onForcer={(motif) => void enregistrer({ motif })}
          onAffilier={tuile === 'interlocuteur' && s.org_id ? (id) => void affilier(id) : undefined} />
      )}
    </>
  );
}

/** Pour l'écran principal : une fiche à modifier se lit d'abord en entier. */
export async function chargerFiche(id: string): Promise<Fiche> {
  return lire<Fiche>(`ged/annuaire/${id.startsWith('PER-') ? 'personnes' : 'organisations'}/${encodeURIComponent(id)}`);
}
export type { Fiche };
